import { AccountsBffService } from '@app/core/services/bff/accounts-bff.service';
import {
  AccountService,
  ClassroomsService,
  CollectionsService,
  ContentService,
  SchoolsService,
  UserServicesService,
  UserServicesBffService,
} from '@core/services';
import {
  FindContentStrategyEnum,
  CollectionDetailsType,
  AccountDetailsType,
  ClassroomDetailsType,
  FindAccountStrategyEnum,
} from 'lingo2-models';

export type TServicesMap = (typeof SrvDefaults.ServicesMap)[keyof typeof SrvDefaults.ServicesMap];
export const DefCollectionDetails: CollectionDetailsType[] = [
  'id',
  'slug',
  'author:sm',
  'access',
  'title',
  'description',
  'excerpt',
  'subject',
  'language',
  'price_tiers',
  'multi_cover:sm',
  'moderation_status',
  'publish_status',
  'stats',
  'valid',
  'visit_info',
  'subject_id',
  'language_id',
];
export const DefAccountDetails: AccountDetailsType[] = [
  'id',
  'country',
  'slug',
  'first_name',
  'last_name',
  'userpic_id',
  'profile',
  'segments',
  'stats',
  'created_at',
  'last_active_at',
  'visit_info',
];
export const DefClassroomsDetails: ClassroomDetailsType[] = [
  'id',
  'slug',
  'author_id',
  'author',
  'author:userpic',
  'access',
  'title',
  'description',
  'stats',
  'type',
  'cover_id',
  'cover:md',
  'participants:some:sm',
  'visit_info',
  'can',
  'last_meeting_id',
  'last_meeting',
  'next_meeting_id',
  'next_meeting',
  'user_service_id',
  // 'user_service',
  'user_service_contract_id',
  // 'user_service_contract',
];

export type EDataLoaderType = 'default' | 'api_bff' | 'api_v1';

/*
 */
export const SrvDefaults = {
  ExtProps: ['author_id', 'author', 'FOREVER'] as const,

  DefAccountDetails,
  DefCollectionDetails,
  DefClassroomsDetails,
  HasContentProps: ['users', 'meetings', 'content', 'filters', 'userServices', 'schools', 'classrooms', 'collections'],

  /* So, now using dynamic loading...
   */
  ServicesMap: {
    userservices_bff: {
      payload_type: 'api_bff',
      strategy: [
        FindContentStrategyEnum.library,
        FindContentStrategyEnum.owned,
        FindContentStrategyEnum.profile,
        'ids' as FindContentStrategyEnum,
      ],
      service: UserServicesBffService,
      method: 'getList',
      paneKey: 'userServices',
      filter: 'userServiceFilter',
      infinity: true,
      _details: (self: any) => [[]],
    },
    userservices: {
      payload_type: 'api_v1',
      strategy: [
        FindContentStrategyEnum.id,
        FindContentStrategyEnum.templates,
        FindContentStrategyEnum.feed,
        FindContentStrategyEnum.favorites,
        FindContentStrategyEnum.collection,
        FindContentStrategyEnum.panel_all,
        FindContentStrategyEnum.related,
        FindContentStrategyEnum.classroom,
        FindContentStrategyEnum.school,
        FindContentStrategyEnum.viewed,
      ],
      service: UserServicesService,
      method: 'getServices',
      paneKey: 'userServices',
      filter: 'userServiceFilter',
      infinity: true,
      _details: (self: any) => [[]],
    },
    accounts_bff: {
      payload_type: 'api_bff',
      strategy: [FindAccountStrategyEnum.teachers],
      service: AccountsBffService,
      method: 'getList',
      paneKey: 'users',
      filter: 'userFilter',
      infinity: true,
      _details: (self: any) => [DefAccountDetails],
    },
    accounts: {
      payload_type: 'api_v1',
      strategy: [
        FindAccountStrategyEnum.favorites,
        FindAccountStrategyEnum.id,
        FindAccountStrategyEnum.my_students,
        FindAccountStrategyEnum.my_teachers,
        FindAccountStrategyEnum.school,
        FindAccountStrategyEnum.subscribers,
        FindAccountStrategyEnum.subscriptions,
      ],
      service: AccountService,
      method: 'findAccounts',
      paneKey: 'users',
      filter: 'userFilter',
      infinity: true,
      _details: (self: any) => [DefAccountDetails],
    },
    schools: {
      payload_type: 'api_v1',
      strategy: [],
      service: SchoolsService,
      method: 'findSchools',
      paneKey: 'schools',
      filter: 'schoolsFilter',
      infinity: true,
      _details: (self: any) => [[]],
    },
    content: {
      payload_type: 'api_v1',
      strategy: [],
      service: ContentService,
      method: 'getContent',
      paneKey: 'content',
      filter: 'contentFilter',
      infinity: false,
      _details: (self: any) => [[]],
    },
    collections: {
      payload_type: 'api_v1',
      strategy: [],
      service: CollectionsService,
      method: 'findCollections',
      paneKey: 'collections',
      filter: 'collectionsFilter',
      infinity: true,
      _details: (self: any) => [DefCollectionDetails],
    },
    classrooms: {
      payload_type: 'api_v1',
      strategy: [],
      service: ClassroomsService,
      method: 'getClassrooms',
      paneKey: 'classrooms',
      filter: 'classroomsFilter',
      infinity: true,
      _details: (self: any) => [DefClassroomsDetails],
    },
  } satisfies Record<
    string,
    {
      strategy: FindContentStrategyEnum[] | FindAccountStrategyEnum[] | string[];
      service: any;
      method: string;
      payload_type: EDataLoaderType;
      paneKey: string;
      filter: string;
      infinity: boolean;
      _details?: (self: any) => any[];
    }
  >,
};
